import React, { useEffect, useState } from 'react';
import { Button, Input, Alert, Divider, Tooltip } from 'antd';
import { SelecteurTauxRc, estTauxRcDuBareme } from './ChampResponsabilite';
import { SendOutlined, CloseCircleOutlined, CheckCircleOutlined, LockOutlined, AimOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import axiosInstance from '../variables/api/axiosInstance';
import TagRc from './TagRc';
const GREEN = '#1a6b3c';
const RED = '#b42318';

/**
 * Panel de négociation RC d'un adversaire - intégré dans la fiche adversaire.
 *
 * Affiche selon l'état courant :
 *   EN_ATTENTE      → saisie % + bouton "Envoyer proposition BNCB"
 *   EN_NEGOCIATION  → bouton "Accepter X%" + bouton "Enregistrer rejet" + champ contre-proposition
 *   REJETEE         → motif du rejet + champ contre-proposition + bouton "Trancher"
 *   TRANCHEE        → verrouillé, affichage en lecture seule
 *
 * Props :
 *  - adversaire : objet victime (avec estAdversaire=true)
 *  - onUpdated  : callback(victimeMiseAJour) après succès d'une action
 */
export default function NegociationRcPanel({ adversaire: adversaireInitial, onUpdated }) {
  /* Le panneau tient sa propre copie de l'adversaire et la remplace par ce que
     renvoie le backend apres chaque action.

     Sans ca, il restait accroche a l'objet fige que la modale lui passait : la
     proposition partait bien, le rappel `onUpdated` rechargeait la liste en
     arriere-plan, mais l'affichage rejouait l'etat d'AVANT - d'ou le « ca a passe
     mais rien n'a change ». Recharger depuis le parent ne suffisait pas : cela
     aurait aussi reinitialise le formulaire de la modale adversaire et ramene
     l'utilisateur a la premiere etape. */
  const { t } = useTranslation('commun');
  const [adversaire, setAdversaire] = useState(adversaireInitial);
  // Point de départ : la dernière proposition, sinon la responsabilité saisie sur la fiche.
  // Une valeur ancienne hors barème (30 %) ne présélectionne rien : il faut choisir.
  const departRc = (a) => {
    const depart = a?.pourcentageRcPropose ?? (a?.tauxRc != null ? Number(a.tauxRc) : null);
    return estTauxRcDuBareme(depart) ? Number(depart) : null;
  };
  const [pourcentage, setPourcentage] = useState(departRc(adversaireInitial));
  const [motifRejet, setMotifRejet] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setAdversaire(adversaireInitial);
    setPourcentage(departRc(adversaireInitial));
    setMotifRejet('');
  }, [adversaireInitial]);

  if (!adversaire?.victimeTrackingId) {
    return (
      <Alert
        type="info"
        showIcon
        message={t('negociationRcPanel.accessibleApresEnregistrement')}
      />
    );
  }

  const etat = adversaire.positionRc || 'EN_ATTENTE';
  const tranchee = etat === 'TRANCHEE';
  const tours = adversaire.nombreToursRc ?? 0;

  const appelerAction = async (body) => {
    setLoading(true);
    try {
      const res = await axiosInstance.patch(
        `/v1/victimes/${adversaire.victimeTrackingId}/position-rc`,
        body
      );
      const maj = res.data?.data;
      if (maj) setAdversaire(maj);
      toast.success(res.data?.message || t('negociationRcPanel.actionEnregistree'));
      onUpdated?.(maj);
    } catch (e) {
      toast.error(e?.response?.data?.message || t('negociationRcPanel.erreurAction'));
    } finally {
      setLoading(false);
    }
  };

  const proposer = () => {
    if (!estTauxRcDuBareme(pourcentage)) {
      return toast.error(t('negociationRcPanel.choisissezTaux'));
    }
    appelerAction({ action: 'PROPOSER', pourcentage });
  };
  const rejeter = () => {
    if (!motifRejet || motifRejet.trim().length < 10) {
      return toast.error(t('negociationRcPanel.motifRejetObligatoire'));
    }
    appelerAction({ action: 'REJETER', motifRejet: motifRejet.trim() });
  };
  const accepter = () => appelerAction({ action: 'ACCEPTER' });
  const trancher = () => {
    if (!estTauxRcDuBareme(pourcentage)) {
      return toast.error(t('negociationRcPanel.choisissezTauxTrancher'));
    }
    appelerAction({ action: 'TRANCHER', pourcentage });
  };

  const identite = `${adversaire.nom || ''} ${adversaire.prenoms || ''}`.trim() ||
    adversaire.immatriculation || t('negociationRcPanel.adversaire');

  return (
    <div style={{
      background: '#fafbfc',
      border: `1px solid #e7e9ee`,
      borderRadius: 10,
      padding: 16,
      marginTop: 8,
    }}>
      {/* ── En-tête : tag + résumé ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#0b0f14' }}>
            <AimOutlined style={{ marginInlineEnd: 6, color: GREEN }} />{t('negociationRcPanel.titre')}
          </span>
          <TagRc
            positionRc={adversaire.positionRc}
            pourcentageRcFinal={adversaire.pourcentageRcFinal}
            pourcentageRcPropose={adversaire.pourcentageRcPropose}
            nombreToursRc={adversaire.nombreToursRc}
            dateDerniereActionRc={adversaire.dateDerniereActionRc}
          />
        </div>
        <div style={{ fontSize: 11.5, color: '#5a6676' }}>
          {tours > 0 && t('negociationRcPanel.tours', { count: tours })}
          {adversaire.dateDerniereActionRc && (
            <> {t('negociationRcPanel.derniereAction', { date: dayjs(adversaire.dateDerniereActionRc).format('DD/MM/YYYY') })}</>
          )}
        </div>
      </div>

      {/* ── Motif rejet précédent (si applicable) ── */}
      {etat === 'REJETEE' && adversaire.motifRejetRc && (
        <Alert
          type="warning"
          showIcon
          message={<span style={{ fontSize: 12.5, fontWeight: 600 }}>{t('negociationRcPanel.motifRejet')}</span>}
          description={<span style={{ fontSize: 12.5 }}>{adversaire.motifRejetRc}</span>}
          style={{ marginBottom: 14 }}
        />
      )}

      {/* ── Proposition courante (si en négo) ── */}
      {etat === 'EN_NEGOCIATION' && (
        <Alert
          type="info"
          showIcon
          message={
            <span style={{ fontSize: 12.5 }}>
              <Trans
                t={t}
                i18nKey="negociationRcPanel.derniereProposition"
                values={{ taux: adversaire.pourcentageRcPropose }}
                components={{ 1: <strong /> }}
              />
              {adversaire.dateDerniereActionRc && (
                <> {t('negociationRcPanel.envoyeeLe', { date: dayjs(adversaire.dateDerniereActionRc).format('DD/MM/YYYY') })}</>
              )}
            </span>
          }
          style={{ marginBottom: 14 }}
        />
      )}

      {/* ── Actions contextuelles ── */}
      {tranchee ? (
        <div style={{ textAlign: 'center', padding: 14, background: '#ecfdf5', borderRadius: 8, border: '1px solid #bbf7d0' }}>
          <LockOutlined style={{ fontSize: 24, color: GREEN, marginBottom: 6 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: GREEN }}>
            {t('negociationRcPanel.verrouillee', { taux: adversaire.pourcentageRcFinal })}
          </div>
          <div style={{ fontSize: 11.5, color: '#5a6676', marginTop: 2 }}>
            {t('negociationRcPanel.pourcentageImmuable', { identite })}
          </div>
        </div>
      ) : (
        <>
          {/* Proposition / Contre-proposition */}
          {(etat === 'EN_ATTENTE' || etat === 'REJETEE') && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#5a6676', marginBottom: 6 }}>
                {etat === 'EN_ATTENTE' ? t('negociationRcPanel.premiereProposition') : t('negociationRcPanel.contreProposition')}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {/* Responsabilité de la partie adverse, au barème : pas de saisie libre. */}
                <SelecteurTauxRc value={pourcentage} onChange={setPourcentage} />
                <Button
                  type="primary" icon={<SendOutlined />}
                  onClick={proposer} loading={loading}
                  disabled={pourcentage == null}
                  style={{ background: GREEN, borderColor: GREEN, minWidth: 180 }}
                >
                  {etat === 'REJETEE' ? t('negociationRcPanel.envoyerContreProposition') : t('negociationRcPanel.envoyerProposition')}
                </Button>
              </div>
              {etat === 'REJETEE' && (
                <>
                  <Divider style={{ margin: '14px 0 10px' }}>{t('negociationRcPanel.ou')}</Divider>
                  <Button
                    block
                    icon={<CheckCircleOutlined />}
                    onClick={trancher}
                    loading={loading}
                    disabled={pourcentage == null}
                  >
                    {pourcentage != null ? t('negociationRcPanel.trancherSur', { taux: pourcentage }) : t('negociationRcPanel.trancherSurCe')}
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Retour de la compagnie (accept / reject) */}
          {etat === 'EN_NEGOCIATION' && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#5a6676', marginBottom: 8 }}>
                {t('negociationRcPanel.retourCompagnie')}
              </div>

              {/* Option 1 : accepter */}
              <Button
                block
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={accepter}
                loading={loading}
                style={{ background: GREEN, borderColor: GREEN, marginBottom: 12 }}
              >
                {t('negociationRcPanel.compagnieAccepte', { taux: adversaire.pourcentageRcPropose })}
              </Button>

              {/* Option 2 : rejet avec motif */}
              <div style={{ fontSize: 11.5, color: '#5a6676', marginBottom: 6 }}>
                {t('negociationRcPanel.compagnieARejete')}
              </div>
              <Input.TextArea
                value={motifRejet}
                onChange={(e) => setMotifRejet(e.target.value)}
                placeholder={t('negociationRcPanel.motifPlaceholder')}
                rows={2}
                maxLength={500}
                showCount
                style={{ marginBottom: 8 }}
              />
              <Button
                block
                danger
                icon={<CloseCircleOutlined />}
                onClick={rejeter}
                loading={loading}
                disabled={motifRejet.trim().length < 10}
              >
                {t('negociationRcPanel.enregistrerRejet')}
              </Button>
            </div>
          )}

          <Divider style={{ margin: '14px 0 8px' }} />
          <div style={{ fontSize: 10.5, color: '#8b95a5' }}>
            <Tooltip title={t('negociationRcPanel.communicationReelle')}>
              <span style={{ cursor: 'help' }}>{t('negociationRcPanel.communicationExterne')}</span>
            </Tooltip>
          </div>
        </>
      )}
    </div>
  );
}
